/*
 * Sticky add to cart bar: shows when the real add to cart button is above the viewport.
 * Pressing it scrolls to the real button and presses it, so required options are still validated.
 */
(() => {
  function init(bar) {
    if (bar.dataset.ready) return;
    bar.dataset.ready = 'true';

    const sectionId = bar.dataset.sectionId;
    const submit = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const button = bar.querySelector('[data-sticky-button]');
    const priceTarget = bar.querySelector('[data-sticky-price]');
    const compareTarget = bar.querySelector('[data-sticky-compare]');
    if (!submit || !button) return;

    const setVisible = (visible) => {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', String(!visible));
      button.tabIndex = visible ? 0 : -1;
    };

    // Visible only after the real button has scrolled past the top of the screen.
    new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 }
    ).observe(submit);

    // The first visible field that failed validation (a name, a letter...), as the block the shopper must fill in.
    const findInvalidField = () => {
      const scope = bar.closest('.shopify-section') || document;
      const candidates = [
        ...scope.querySelectorAll(
          '[aria-invalid="true"], input:invalid, textarea:invalid, select:invalid, [class*="error"], [class*="invalid"]'
        ),
      ].filter((el) => {
        if (bar.contains(el) || !el.getClientRects().length) return false;
        const style = getComputedStyle(el);
        if (style.visibility === 'hidden' || style.display === 'none') return false;
        const isField = el.matches('input, textarea, select');
        return isField || el.textContent.trim().length > 0;
      });
      if (!candidates.length) return null;
      candidates.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
      const first = candidates[0];
      // Go up to the block of the option (label, field and message together), so the shopper sees what to fill in.
      return first.closest('[class*="gpo-"], [class*="globo"], fieldset, .product-form__input') || first;
    };

    const scrollToField = (field) => {
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 96;
      // Extra room above the block, so its label and the text above it stay visible.
      const top = field.getBoundingClientRect().top + window.scrollY - headerHeight - 90;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };

    // The first selection option of the product (colour, size, stone...), if it has any visible.
    const findFirstOption = () => {
      const scope = bar.closest('.shopify-section') || document;
      const options = [
        ...scope.querySelectorAll(
          '.product-form__input, variant-selects fieldset, [class*="globo-swatch-product-detail"], [class*="swatch-option"], [class*="gsw-"]'
        ),
      ].filter((el) => !bar.contains(el) && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      if (!options.length) return null;
      options.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
      return options[0].closest('[id^="shopify-block"], .product-form__input') || options[0];
    };

    // Products with options: the first press takes the shopper to the first option so they can choose.
    // Once they have chosen (or have been taken there), the next press adds to the cart.
    let optionsSeen = false;
    const section = bar.closest('.shopify-section') || document;
    section.addEventListener('change', () => (optionsSeen = true), true);
    section.addEventListener('click', (event) => {
      if (event.target.closest('[class*="swatch"], .product-form__input, variant-selects')) optionsSeen = true;
    });

    button.addEventListener('click', () => {
      if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
      const firstOption = optionsSeen ? null : findFirstOption();
      if (firstOption) {
        optionsSeen = true;
        scrollToField(firstOption);
        return;
      }
      // Press the real button where the shopper is. If an option is missing, take them to it.
      submit.click();
      let tries = 0;
      const check = () => {
        const field = findInvalidField();
        if (field) {
          scrollToField(field);
          return;
        }
        if (++tries < 8) setTimeout(check, 150);
      };
      setTimeout(check, 150);
    });

    // Keep the price in sync with the selected variant.
    const priceBox = document.getElementById(`price-${sectionId}`);
    if (priceBox && priceTarget) {
      const sync = () => {
        const current = priceBox.querySelector('.price-item--last, .price-item--sale, .price-item--regular');
        if (current) priceTarget.textContent = current.textContent.trim();
        if (compareTarget) {
          // The struck-through price only exists while the selected variant is on sale.
          const compare = priceBox.querySelector('.price__sale s.price-item--regular');
          compareTarget.hidden = !compare;
          if (compare) compareTarget.textContent = compare.textContent.trim();
        }
      };
      new MutationObserver(sync).observe(priceBox, { childList: true, subtree: true, characterData: true });
      sync();
    }

    // Keep the label in sync (sold out / unavailable variants).
    const label = submit.querySelector('span');
    if (label) {
      const syncLabel = () => {
        const text = button.querySelector('.product-sticky-atc__button-text');
        if (text) text.textContent = label.textContent.trim();
        else button.textContent = label.textContent.trim();
        button.disabled = submit.disabled;
      };
      new MutationObserver(syncLabel).observe(submit, { attributes: true, childList: true, subtree: true });
      syncLabel();
    }
  }

  const initAll = () => document.querySelectorAll('[data-product-sticky-atc]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
